/**
 * V1 per-product application fields (W2, primary, conforming scope).
 */

import { V1_PRODUCT_IDS } from './products';

const shared = {
  legalName: {
    id: 'legalName',
    question: 'What is your full legal name?',
    type: 'text',
    required: true,
    step: 1,
  },
  dateOfBirth: {
    id: 'dateOfBirth',
    question: 'What is your date of birth?',
    type: 'date',
    required: true,
    step: 1,
  },
  email: {
    id: 'email',
    question: 'What is your email address?',
    subtitle: 'Lenders you choose will use this to contact you.',
    type: 'email',
    required: true,
    step: 1,
  },
  phone: {
    id: 'phone',
    question: 'What is your mobile phone number?',
    type: 'tel',
    required: true,
    step: 1,
  },
  currentAddress: {
    id: 'currentAddress',
    question: 'What is your current street address?',
    type: 'text',
    required: true,
    step: 1,
  },
  occupancyAttestation: {
    id: 'occupancyAttestation',
    question: 'Do you intend to occupy this property as your primary residence?',
    type: 'radio',
    required: true,
    step: 1,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  housingStatus: {
    id: 'housingStatus',
    question: 'What is your current housing situation?',
    type: 'radio',
    required: true,
    step: 2,
    options: [
      { value: 'rent', label: 'Renting' },
      { value: 'own', label: 'Own with mortgage' },
      { value: 'own-free', label: 'Own free and clear' },
      { value: 'other', label: 'Other' },
    ],
  },
  monthlyHousingPayment: {
    id: 'monthlyHousingPayment',
    question: 'What is your monthly housing payment (rent or mortgage)?',
    type: 'currency',
    required: true,
    step: 2,
    showIf: (a) => a.housingStatus && a.housingStatus !== 'own-free',
  },
  employerName: {
    id: 'employerName',
    question: 'What is your current employer name?',
    type: 'text',
    required: true,
    step: 3,
  },
  employmentStartDate: {
    id: 'employmentStartDate',
    question: 'When did you start this job?',
    subtitle: 'Approximate start date is fine.',
    type: 'date',
    required: true,
    step: 3,
  },
  grossMonthlyIncome: {
    id: 'grossMonthlyIncome',
    question: 'What is your gross monthly income (before taxes)?',
    type: 'currency',
    required: true,
    step: 3,
  },
  monthlyDebtPayments: {
    id: 'monthlyDebtPayments',
    question: 'What are your total monthly debt payments?',
    subtitle: 'Credit cards, auto, student loans, and other installment debt—not utilities.',
    type: 'currency',
    required: true,
    step: 3,
  },
  liquidAssets: {
    id: 'liquidAssets',
    question: 'About how much do you have in liquid assets?',
    subtitle: 'Checking, savings, and similar accounts for closing.',
    type: 'currency',
    required: false,
    step: 3,
  },
  giftFunds: {
    id: 'giftFunds',
    question: 'Will any of your down payment or closing funds be a gift?',
    type: 'radio',
    required: true,
    step: 3,
    options: [
      { value: 'no', label: 'No' },
      { value: 'yes', label: 'Yes' },
    ],
  },
  propertyAddress: {
    id: 'propertyAddress',
    question: 'What is the property address?',
    subtitle: 'Enter “TBD” if you have not identified the home yet.',
    type: 'text',
    required: true,
    step: 2,
  },
  purchasePrice: {
    id: 'purchasePrice',
    question: 'Estimated purchase price',
    type: 'currency',
    required: true,
    step: 2,
  },
  downPaymentAmount: {
    id: 'downPaymentAmount',
    question: 'Down payment amount',
    type: 'currency',
    required: true,
    step: 2,
  },
  estimatedTaxesMonthly: {
    id: 'estimatedTaxesMonthly',
    question: 'Estimated property taxes per month (optional)',
    type: 'currency',
    required: false,
    step: 2,
  },
  estimatedInsuranceMonthly: {
    id: 'estimatedInsuranceMonthly',
    question: 'Estimated homeowners insurance per month (optional)',
    type: 'currency',
    required: false,
    step: 2,
  },
  hoaMonthly: {
    id: 'hoaMonthly',
    question: 'Monthly HOA dues (if any)',
    type: 'currency',
    required: false,
    step: 2,
  },
  estimatedHomeValue: {
    id: 'estimatedHomeValue',
    question: 'What is your estimated home value?',
    type: 'currency',
    required: true,
    step: 2,
  },
  currentLoanBalance: {
    id: 'currentLoanBalance',
    question: 'What is your current loan balance (payoff amount)?',
    type: 'currency',
    required: true,
    step: 2,
  },
  currentInterestRate: {
    id: 'currentInterestRate',
    question: 'What is your current interest rate?',
    subtitle: 'Enter as a percent, e.g. 6.5',
    type: 'number',
    required: true,
    step: 2,
  },
  currentMonthlyPI: {
    id: 'currentMonthlyPI',
    question: 'What is your current principal & interest payment?',
    type: 'currency',
    required: true,
    step: 2,
  },
  hasJuniorLien: {
    id: 'hasJuniorLien',
    question: 'Do you have a second mortgage or HELOC on the property?',
    type: 'radio',
    required: true,
    step: 2,
    options: [
      { value: 'no', label: 'No' },
      { value: 'yes', label: 'Yes' },
    ],
  },
  juniorLienBalance: {
    id: 'juniorLienBalance',
    question: 'Combined balance on second lien / HELOC',
    type: 'currency',
    required: true,
    step: 2,
    showIf: (a) => a.hasJuniorLien === 'yes',
  },
  fhaCaseNumber: {
    id: 'fhaCaseNumber',
    question: 'FHA case number (if you have it)',
    type: 'text',
    required: false,
    step: 2,
  },
  fhaFirstPaymentDue: {
    id: 'fhaFirstPaymentDue',
    question: 'Date of first payment due on your current FHA loan',
    type: 'date',
    required: true,
    step: 2,
  },
  fhaPaymentsMade: {
    id: 'fhaPaymentsMade',
    question: 'How many monthly payments have you made on this FHA loan?',
    type: 'number',
    required: true,
    step: 2,
  },
  currentMonthlyMip: {
    id: 'currentMonthlyMip',
    question: 'Current monthly FHA mortgage insurance (MIP)',
    type: 'currency',
    required: false,
    step: 2,
  },
  paymentHistorySelfCert: {
    id: 'paymentHistorySelfCert',
    question: 'In the last 12 months, were all mortgage payments made on time?',
    subtitle: 'Lenders will verify payment history.',
    type: 'radio',
    required: true,
    step: 2,
    options: [
      { value: 'yes', label: 'Yes — no 30-day late payments' },
      { value: 'no', label: 'No — I had late payments' },
    ],
  },
  vaLoanNumber: {
    id: 'vaLoanNumber',
    question: 'VA loan number (if known)',
    type: 'text',
    required: false,
    step: 2,
  },
  vaFirstPaymentDue: {
    id: 'vaFirstPaymentDue',
    question: 'Date of first payment due on your current VA loan',
    type: 'date',
    required: true,
    step: 2,
  },
  vaPaymentsMade: {
    id: 'vaPaymentsMade',
    question: 'How many monthly payments have you made on this VA loan?',
    type: 'number',
    required: true,
    step: 2,
  },
  coeOnFile: {
    id: 'coeOnFile',
    question: 'Do you already have a Certificate of Eligibility (COE) on file with a lender?',
    type: 'radio',
    required: true,
    step: 2,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No / not sure' },
    ],
  },
  refiGoalDetail: {
    id: 'refiGoalDetail',
    question: 'What is your main goal for this refinance?',
    type: 'radio',
    required: true,
    step: 2,
    options: [
      { value: 'lower-payment', label: 'Lower payment or rate' },
      { value: 'shorter-term', label: 'Shorter loan term' },
    ],
  },
  cashOutAmount: {
    id: 'cashOutAmount',
    question: 'How much cash do you want to take out?',
    type: 'currency',
    required: true,
    step: 2,
  },
  cashOutPurpose: {
    id: 'cashOutPurpose',
    question: 'Primary purpose of cash out',
    type: 'radio',
    required: true,
    step: 2,
    options: [
      { value: 'home-improvement', label: 'Home improvement' },
      { value: 'debt-consolidation', label: 'Debt consolidation' },
      { value: 'other', label: 'Other' },
    ],
  },
  hadFhaLoanBefore: {
    id: 'hadFhaLoanBefore',
    question: 'Have you had an FHA-insured loan before?',
    type: 'radio',
    required: true,
    step: 4,
    options: [
      { value: 'no', label: 'No' },
      { value: 'yes', label: 'Yes' },
    ],
  },
  bankruptcyLast2Years: {
    id: 'bankruptcyLast2Years',
    question: 'Any bankruptcy in the last 2 years?',
    type: 'radio',
    required: true,
    step: 4,
    options: [
      { value: 'no', label: 'No' },
      { value: 'yes', label: 'Yes' },
    ],
  },
  foreclosureLast3Years: {
    id: 'foreclosureLast3Years',
    question: 'Any foreclosure in the last 3 years?',
    type: 'radio',
    required: true,
    step: 4,
    options: [
      { value: 'no', label: 'No' },
      { value: 'yes', label: 'Yes' },
    ],
  },
};

const pick = (...keys) => keys.map((k) => shared[k]);

export const V1_PRODUCT_FIELD_KEYS = {
  [V1_PRODUCT_IDS.CONVENTIONAL_PURCHASE]: [
    'legalName', 'dateOfBirth', 'email', 'phone', 'currentAddress', 'occupancyAttestation',
    'propertyAddress', 'purchasePrice', 'downPaymentAmount', 'estimatedTaxesMonthly',
    'estimatedInsuranceMonthly', 'hoaMonthly', 'housingStatus', 'monthlyHousingPayment',
    'employerName', 'employmentStartDate', 'grossMonthlyIncome', 'monthlyDebtPayments',
    'liquidAssets', 'giftFunds',
  ],
  [V1_PRODUCT_IDS.FHA_PURCHASE]: [
    'legalName', 'dateOfBirth', 'email', 'phone', 'currentAddress', 'occupancyAttestation',
    'propertyAddress', 'purchasePrice', 'downPaymentAmount', 'estimatedTaxesMonthly',
    'estimatedInsuranceMonthly', 'hoaMonthly', 'housingStatus', 'monthlyHousingPayment',
    'employerName', 'employmentStartDate', 'grossMonthlyIncome', 'monthlyDebtPayments',
    'liquidAssets', 'giftFunds', 'hadFhaLoanBefore', 'bankruptcyLast2Years', 'foreclosureLast3Years',
  ],
  [V1_PRODUCT_IDS.FHA_STREAMLINE]: [
    'legalName', 'dateOfBirth', 'email', 'phone', 'currentAddress', 'occupancyAttestation',
    'propertyAddress', 'currentLoanBalance', 'currentInterestRate', 'currentMonthlyPI',
    'currentMonthlyMip', 'fhaCaseNumber', 'fhaFirstPaymentDue', 'fhaPaymentsMade',
    'paymentHistorySelfCert',
  ],
  [V1_PRODUCT_IDS.VA_IRRRL]: [
    'legalName', 'dateOfBirth', 'email', 'phone', 'currentAddress', 'occupancyAttestation',
    'propertyAddress', 'currentLoanBalance', 'currentInterestRate', 'currentMonthlyPI',
    'vaLoanNumber', 'vaFirstPaymentDue', 'vaPaymentsMade', 'coeOnFile', 'paymentHistorySelfCert',
  ],
  [V1_PRODUCT_IDS.CONVENTIONAL_RATE_TERM]: [
    'legalName', 'dateOfBirth', 'email', 'phone', 'currentAddress', 'occupancyAttestation',
    'propertyAddress', 'estimatedHomeValue', 'currentLoanBalance', 'currentInterestRate',
    'currentMonthlyPI', 'hasJuniorLien', 'juniorLienBalance', 'refiGoalDetail',
    'housingStatus', 'monthlyHousingPayment', 'employerName', 'employmentStartDate',
    'grossMonthlyIncome', 'monthlyDebtPayments', 'liquidAssets',
  ],
  [V1_PRODUCT_IDS.CONVENTIONAL_CASH_OUT]: [
    'legalName', 'dateOfBirth', 'email', 'phone', 'currentAddress', 'occupancyAttestation',
    'propertyAddress', 'estimatedHomeValue', 'currentLoanBalance', 'currentInterestRate',
    'currentMonthlyPI', 'hasJuniorLien', 'juniorLienBalance', 'cashOutAmount', 'cashOutPurpose',
    'housingStatus', 'monthlyHousingPayment', 'employerName', 'employmentStartDate',
    'grossMonthlyIncome', 'monthlyDebtPayments', 'liquidAssets',
  ],
};

export const getV1FieldDef = (fieldId) => shared[fieldId] || null;

export const getVisibleV1ProductFields = (productId, answers = {}) => {
  const keys = V1_PRODUCT_FIELD_KEYS[productId] || [];
  return keys
    .map((id) => shared[id])
    .filter(Boolean)
    .filter((f) => !f.showIf || f.showIf(answers));
};

export const isV1ProductFieldsComplete = (productId, answers = {}) => {
  const visible = getVisibleV1ProductFields(productId, answers);
  return visible
    .filter((f) => f.required)
    .every((f) => {
      const v = answers[f.id];
      return v !== undefined && v !== null && String(v).trim() !== '';
    });
};

export const getV1FieldCompletenessPercent = (productId, answers = {}) => {
  const visible = getVisibleV1ProductFields(productId, answers);
  const required = visible.filter((f) => f.required);
  if (required.length === 0) return 100;
  const done = required.filter((f) => {
    const v = answers[f.id];
    return v !== undefined && v !== null && String(v).trim() !== '';
  }).length;
  return Math.round((done / required.length) * 100);
};
